import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useContractRequestList } from "~/services/business/contractRequest";
import type { CdRequestStatus } from "~/types/common";

/** 탭 3: 진행 중인 서명 요청 — API 연동 */

type Filter = "all" | "in-progress" | "rejected";

const STATUS_FILTER_MAP: Record<Exclude<Filter, "all">, CdRequestStatus[]> = {
  "in-progress": ["REQUEST_STATUS_SENT", "REQUEST_STATUS_OPENED"],
  rejected: [
    "REQUEST_STATUS_REJECTED",
    "REQUEST_STATUS_EXPIRED",
    "REQUEST_STATUS_CANCELED",
  ],
};

/** 완료(서명/체결)된 요청은 "체결 완료" 탭에서 보므로 진행중 탭에서는 제외한다. */
const DONE_STATUSES: CdRequestStatus[] = [
  "REQUEST_STATUS_SIGNED",
  "REQUEST_STATUS_COMPLETED",
];

export const ContractTabRequests = () => {
  const { history } = useRouter();
  const [filter, setFilter] = useState<Filter>("all");

  const { data: allRows = [], isLoading } = useContractRequestList();

  // 완료본은 체결 완료 탭으로 이동 → 이 탭은 미완료(진행중·거절·만료·취소)만
  const rows = allRows.filter(
    (r) => !DONE_STATUSES.includes(r.cdRequestStatus),
  );

  const goSend = () =>
    history.push("/business/layout/contract-request-send" as any);
  const goDetail = (requestSeq: number) =>
    history.push(
      `/business/layout/contract-request-detail?requestSeq=${requestSeq}` as any,
    );

  const filtered = rows.filter((r) => {
    if (filter === "all") return true;
    return STATUS_FILTER_MAP[filter].includes(r.cdRequestStatus);
  });

  const counts = {
    all: rows.length,
    inProgress: rows.filter((r) =>
      STATUS_FILTER_MAP["in-progress"].includes(r.cdRequestStatus),
    ).length,
    rejected: rows.filter((r) =>
      STATUS_FILTER_MAP.rejected.includes(r.cdRequestStatus),
    ).length,
  };

  return (
    <div>
      {/* 상단 액션 + 필터 — 스크롤 되는 부모 안에서 sticky 로 위에 박힘 */}
      <div className="flex items-center justify-between px-4 py-3 gap-2 bg-[#F5F5F5] sticky top-0 z-10">
        <div className="flex gap-2 overflow-x-auto flex-1">
          <FilterChip
            active={filter === "all"}
            onClick={() => setFilter("all")}
          >
            전체 ({counts.all})
          </FilterChip>
          <FilterChip
            active={filter === "in-progress"}
            onClick={() => setFilter("in-progress")}
          >
            진행중 ({counts.inProgress})
          </FilterChip>
          <FilterChip
            active={filter === "rejected"}
            onClick={() => setFilter("rejected")}
          >
            거절 ({counts.rejected})
          </FilterChip>
        </div>
        <button
          type="button"
          onClick={goSend}
          className="shrink-0 bg-[#FF9934] text-white text-[12px] font-semibold rounded-full px-3 py-1.5"
        >
          + 발송
        </button>
      </div>

      {/* 목록 */}
      <div className="mx-4 space-y-2">
        {isLoading && (
          <div className="text-center text-[#999] text-[13px] py-12">
            불러오는 중...
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <div className="text-center text-[#999] text-[13px] py-12">
            해당 항목이 없어요
          </div>
        )}
        {filtered.map((req) => (
          <div
            key={req.requestSeq}
            onClick={() => goDetail(req.requestSeq)}
            className="bg-white rounded-lg border border-[#e9e9e9] p-3 cursor-pointer active:bg-[#FAFAFA]"
          >
            <div className="flex items-center gap-2 mb-1">
              <StatusIcon status={req.cdRequestStatus} />
              <div className="text-[14px] font-semibold text-[#111] flex-1">
                {req.userName} {req.petName ? `(${req.petName})` : ""}
              </div>
              <ActionButton status={req.cdRequestStatus} />
            </div>
            <div className="text-[12px] text-[#666] ml-7">
              {req.contractName}
            </div>
            <div className="text-[11px] text-[#999] mt-1 ml-7">
              발송 {req.dtSent?.slice(0, 16).replace("T", " ")}
              {req.dtCompleted &&
                ` · 완료 ${req.dtCompleted.slice(0, 16).replace("T", " ")}`}
            </div>
            {req.rejectReason && (
              <div className="mt-2 ml-7 text-[11px] text-[#F24747] bg-[#FFF1F1] rounded p-2">
                💬 {req.rejectReason}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const FilterChip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`shrink-0 text-[12px] font-semibold rounded-full px-3 py-1.5 transition-colors ${
      active
        ? "bg-[#FF9934] text-white"
        : "bg-white text-[#666] border border-[#E9E9E9]"
    }`}
  >
    {children}
  </button>
);

const STATUS_MAP: Record<
  CdRequestStatus,
  { icon: string; label: string; color: string }
> = {
  REQUEST_STATUS_SENT: {
    icon: "📨",
    label: "서명 대기",
    color: "text-[#FF9934]",
  },
  REQUEST_STATUS_OPENED: {
    icon: "👁",
    label: "열람함",
    color: "text-[#7D6DFF]",
  },
  REQUEST_STATUS_SIGNED: {
    icon: "✏️",
    label: "보호자 서명",
    color: "text-[#22A55B]",
  },
  REQUEST_STATUS_COMPLETED: {
    icon: "✅",
    label: "완료",
    color: "text-[#22A55B]",
  },
  REQUEST_STATUS_REJECTED: {
    icon: "❌",
    label: "거절",
    color: "text-[#F24747]",
  },
  REQUEST_STATUS_EXPIRED: { icon: "⏰", label: "만료", color: "text-[#999]" },
  REQUEST_STATUS_CANCELED: { icon: "↩", label: "취소", color: "text-[#999]" },
};

const StatusIcon = ({ status }: { status: CdRequestStatus }) => {
  const { icon } = STATUS_MAP[status] ?? STATUS_MAP.REQUEST_STATUS_SENT;
  return <div className="text-[16px] w-5 text-center">{icon}</div>;
};

const ActionButton = ({ status }: { status: CdRequestStatus }) => {
  const { label } = STATUS_MAP[status] ?? STATUS_MAP.REQUEST_STATUS_SENT;
  return <span className="text-[11px] font-semibold text-[#999]">{label}</span>;
};
